"use client"

import type React from "react"
import { useState } from "react"
import { Save, Eye, EyeOff } from "lucide-react"
import toast from "react-hot-toast"

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState("general")
  const [showApiKeys, setShowApiKeys] = useState(false)
  const [settings, setSettings] = useState({
    // General Settings
    siteName: "Anmol Furniture Bazar",
    siteDescription: "Premium furniture for your dream home",
    contactEmail: "anmolfurniturebazaar@gmail.com",
    contactPhone: "+91 8010271912",
    address: "Anmol Furniture Bazar, Virar East, Thane, Maharashtra 401305",

    // Payment Settings
    razorpayKeyId: "",
    razorpayKeySecret: "",
    enableCOD: true,
    minOrderForFreeShipping: 25000,
    shippingCharge: 500,

    // Email Settings
    smtpHost: "",
    smtpPort: 587,
    smtpUser: "",
    smtpPassword: "",

    // SEO Settings
    metaTitle: "Anmol Furniture Bazaar - Premium Furniture Store",
    metaDescription:
      "Shop premium furniture online. Beds, sofas, dining sets, wardrobes and more. Free delivery, warranty, easy returns.",
    metaKeywords: "furniture, beds, sofas, dining, wardrobes, home decor",

    // Social Media
    facebookUrl: "",
    twitterUrl: "",
    instagramUrl: "",
    youtubeUrl: "",

    // Business Settings
    businessHours: "Mon-Sat: 9:00 AM - 8:00 PM, Sun: 10:00 AM - 6:00 PM",
    returnPolicy: "30-day hassle-free return policy",
    warrantyInfo: "Up to 3 years warranty on select items",

    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    orderUpdates: true,
    promotionalEmails: true,
  })

  const tabs = [
    { id: "general", label: "General" },
    { id: "payment", label: "Payment" },
    { id: "email", label: "Email" },
    { id: "seo", label: "SEO" },
    { id: "social", label: "Social Media" },
    { id: "business", label: "Business" },
    { id: "notifications", label: "Notifications" },
  ]

  const handleSave = async () => {
    try {
      // Here you would save settings to Firebase or your backend
      toast.success("Settings saved successfully!")
    } catch (error) {
      console.error("Error saving settings:", error)
      toast.error("Failed to save settings")
    }
  }

  const handleInputChange = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
          <input
            type="text"
            value={settings.siteName}
            onChange={(e) => handleInputChange("siteName", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
          <input
            type="email"
            value={settings.contactEmail}
            onChange={(e) => handleInputChange("contactEmail", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
          <input
            type="tel"
            value={settings.contactPhone}
            onChange={(e) => handleInputChange("contactPhone", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
          <input
            type="text"
            value={settings.businessHours}
            onChange={(e) => handleInputChange("businessHours", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
        <textarea
          value={settings.siteDescription}
          onChange={(e) => handleInputChange("siteDescription", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
        <textarea
          value={settings.address}
          onChange={(e) => handleInputChange("address", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
    </div>
  )

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> Keep your API keys secure. Never share them publicly.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Razorpay Key ID</label>
          <div className="relative">
            <input
              type={showApiKeys ? "text" : "password"}
              value={settings.razorpayKeyId}
              onChange={(e) => handleInputChange("razorpayKeyId", e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="rzp_test_..."
            />
            <button
              type="button"
              onClick={() => setShowApiKeys(!showApiKeys)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Razorpay Key Secret</label>
          <input
            type={showApiKeys ? "text" : "password"}
            value={settings.razorpayKeySecret}
            onChange={(e) => handleInputChange("razorpayKeySecret", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Your secret key"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Min Order for Free Shipping (₹)</label>
          <input
            type="number"
            value={settings.minOrderForFreeShipping}
            onChange={(e) => handleInputChange("minOrderForFreeShipping", Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Charge (₹)</label>
          <input
            type="number"
            value={settings.shippingCharge}
            onChange={(e) => handleInputChange("shippingCharge", Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.enableCOD}
            onChange={(e) => handleInputChange("enableCOD", e.target.checked)}
            className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Enable Cash on Delivery</span>
        </label>
      </div>
    </div>
  )

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
          <input
            type="text"
            value={settings.smtpHost}
            onChange={(e) => handleInputChange("smtpHost", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="smtp.gmail.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
          <input
            type="number"
            value={settings.smtpPort}
            onChange={(e) => handleInputChange("smtpPort", Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
          <input
            type="email"
            value={settings.smtpUser}
            onChange={(e) => handleInputChange("smtpUser", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
          <input
            type="password"
            value={settings.smtpPassword}
            onChange={(e) => handleInputChange("smtpPassword", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </div>
  )

  const renderSEOSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
        <input
          type="text"
          value={settings.metaTitle}
          onChange={(e) => handleInputChange("metaTitle", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
        <textarea
          value={settings.metaDescription}
          onChange={(e) => handleInputChange("metaDescription", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords</label>
        <input
          type="text"
          value={settings.metaKeywords}
          onChange={(e) => handleInputChange("metaKeywords", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="keyword1, keyword2, keyword3"
        />
      </div>
    </div>
  )

  const renderSocialSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
          <input
            type="url"
            value={settings.facebookUrl}
            onChange={(e) => handleInputChange("facebookUrl", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://facebook.com/yourpage"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
          <input
            type="url"
            value={settings.twitterUrl}
            onChange={(e) => handleInputChange("twitterUrl", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://twitter.com/yourhandle"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
          <input
            type="url"
            value={settings.instagramUrl}
            onChange={(e) => handleInputChange("instagramUrl", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://instagram.com/yourhandle"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">YouTube URL</label>
          <input
            type="url"
            value={settings.youtubeUrl}
            onChange={(e) => handleInputChange("youtubeUrl", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="https://youtube.com/yourchannel"
          />
        </div>
      </div>
    </div>
  )

  const renderBusinessSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Return Policy</label>
        <textarea
          value={settings.returnPolicy}
          onChange={(e) => handleInputChange("returnPolicy", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Warranty Information</label>
        <textarea
          value={settings.warrantyInfo}
          onChange={(e) => handleInputChange("warrantyInfo", e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.emailNotifications}
            onChange={(e) => handleInputChange("emailNotifications", e.target.checked)}
            className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Enable Email Notifications</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.smsNotifications}
            onChange={(e) => handleInputChange("smsNotifications", e.target.checked)}
            className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Enable SMS Notifications</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.orderUpdates}
            onChange={(e) => handleInputChange("orderUpdates", e.target.checked)}
            className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Send Order Updates</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={settings.promotionalEmails}
            onChange={(e) => handleInputChange("promotionalEmails", e.target.checked)}
            className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Send Promotional Emails</span>
        </label>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return renderGeneralSettings()
      case "payment":
        return renderPaymentSettings()
      case "email":
        return renderEmailSettings()
      case "seo":
        return renderSEOSettings()
      case "social":
        return renderSocialSettings()
      case "business":
        return renderBusinessSettings()
      case "notifications":
        return renderNotificationSettings()
      default:
        return renderGeneralSettings()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your store configuration</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Save className="w-4 h-4" />
          <span>Save Changes</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <nav className="space-y-1 p-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-primary-100 text-primary-700 font-medium"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {tabs.find((tab) => tab.id === activeTab)?.label} Settings
            </h2>
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminSettings
